/*
 * Copyright © 2016-2020 The Thingsboard Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import './croplevel-overview.scss';

/* eslint-disable import/no-unresolved, import/default */

import pageTemplate from './croplevel-overview.tpl.html';
import {displayTelemetryTooltip} from "../../../../../utils/tooltip-utility";

/* eslint-enable import/no-unresolved, import/default */

export default angular.module('agrolog.client.cropLevelOverview', [])
    .directive('agCropLevelOverview', CropLevelOverview)
    .name;

/*@ngInject*/
function CropLevelOverview() {
    return {
        restrict: "E",
        scope: true,
        bindToController: {
            ctx: '=',
            isDashboard: '=',
            providedAssetId: '='
        },
        controller: CropLevelController,
        controllerAs: 'vm',
        templateUrl: pageTemplate
    };
}

/* eslint-disable angular/angularelement */

/*@ngInject*/
function CropLevelController($scope, $filter, $translate, $q, $log, $compile, types, agTypes, agUtils, siloService, unitService, userService, attributeService) {

    let vm = this;

    vm.cropLevelLoading = true;
    vm.loading = false;
    vm.agTypes = agTypes;
    vm.showData = userService.getAuthority() === 'TENANT_ADMIN' || userService.getAuthority() === 'CUSTOMER_USER';
    vm.siloId = null;
    vm.silo = null;
    vm.levelIndicator = null;
    vm.siloData = null;

    vm.cropLevelLegendConfig = {
        displayMaxThreshold: true,
        displayMinThreshold: true
    };
    vm.cropLevelSubscriptionOptions = {
        callbacks: {
            onDataUpdated: onDataUpdated,
            onDataUpdateError: onDataUpdateError,
            dataLoading: (subscription) => {
                vm.cropLevelLoading = subscription.loadingData;
            }
        },
        useDashboardTimewindow: false
    };

    vm.flotSettings = {
        shadowSize: 0,
        yaxis: {
            showLabels: false
        },
        xaxis: {
            showLabels: false
        },
        grid: {
            outlineWidth: 0,
            margin: 0,
            minBorderMargin: 0,
            tickColor: 'rgba(255, 255, 255, 0)' // Workaround to hide grid lines
        }
    };

    vm.flotKeySettings = {
        lineWidth: 1
    };

    vm.displayValueTooltip = displayValueTooltip;
    vm.getStatusColor = getStatusColor;
    vm.tempColor = tempColor;

    $scope.$watch('vm.ctx', () => {
        if (vm.ctx && vm.showData) {
            vm.ctx.widgetActions = [];
            vm.ctx.hideTitlePanel = false;

            vm.silo = null;
            vm.levelIndicator = null;

            if (vm.providedAssetId) {
                vm.siloId = vm.providedAssetId;
            } else {
                if (vm.ctx.stateController) {
                    vm.siloId = vm.ctx.stateController.getStateParams().entityId;
                }

                $scope.$watch(function () {
                        return vm.ctx.stateController.getStateParams().entityId;
                    },
                    function (newSiloId, prevSiloId) {
                        if (newSiloId && !angular.equals(newSiloId, prevSiloId)) {
                            vm.siloId = newSiloId;
                            vm.hideCropLevel = false;
                            loadSilo();
                        }
                    }
                );
            }

            loadSilo();
        }
    });

    $scope.$watch('vm.silo', () => {
        if (!vm.silo || !vm.silo.id) return;
        //$log.info('--- silo loaded')
        vm.cropLevelLoading = true;
        var cropLevelSubscriptionsInfo = [{
            type: types.datasourceType.entity,
            entityType: types.entityType.asset,
            entityId: vm.silo.id.id,
            entityName: vm.silo.name,
            timeseries: [
                { name: "crop-level-min", label: "Min Level" },
                { name: "crop-level-avg", label: "Avg Level" },
                { name: "crop-level-max", label: "Max Level" },
            ],
            attributes: []
        }];
        
        cropLevelSubscriptionsInfo[0].timeWindowConfig = angular.copy(vm.ctx.dashboardTimewindow);
        cropLevelSubscriptionsInfo[0].timeWindowConfig.aggregation.type = types.aggregation.none.value;
        vm.cropLevelFlotSubscriptionsInfo = angular.copy(cropLevelSubscriptionsInfo);
        
        cropLevelSubscriptionsInfo[0].timeseries.push(
            { name: "crop-points", label: "Crop Points" },
            { name: "crop-volume", label: "Crop Volume" },
            { name: "crop-weight", label: "Crop Weight" },
            { name: "crop_type", label: "Crop Type" },
            { name: "silo-volume", label: "Silo Volume" }
        )
        
        vm.cropLevelSubscriptionOptions.timeWindowConfig = angular.copy(vm.ctx.dashboardTimewindow);
        vm.cropLevelSubscriptionOptions.timeWindowConfig.aggregation.type = types.aggregation.none.value;
        //vm.cropLevelSubscriptionOptions.timeWindowConfig.aggregation.limit = 1;
        vm.ctx.subscriptionApi.createSubscriptionFromInfo(
            types.widgetType.timeseries.value, cropLevelSubscriptionsInfo, vm.cropLevelSubscriptionOptions, false, true).then(
            (subscription) => {
                vm.subscription = subscription;
            }
        );
    });

    $scope.$watch(() => {
        return vm.silo ? (vm.silo.label || vm.silo.name) : null;
    }, (title) => {
        if (vm.showData && vm.ctx && title) {
            vm.ctx.widgetTitle = $translate.instant('ag.silo.silo-crop-level') + ': ' + title;
        }
    });

    $scope.$on('dashboardTimewindowChanged', (event, newDashboardTimewindow) => {
        if (vm.subscription) {
            var newTimewindowConfig = angular.copy(newDashboardTimewindow);
            newTimewindowConfig.aggregation.type = types.aggregation.none.value;
            newTimewindowConfig.aggregation.limit = 1;
            vm.subscription.timeWindowConfig = newTimewindowConfig;
        }
    });

    $scope.$on('$destroy', () => {
        reset();
    });

    agUtils.getCropNames().then(
        (response) => {
            vm.cropNames = [];
            var cropNames = response.jsonValue;
            for (var property in cropNames) {
                var cropNameObj = {};
                cropNameObj.nameId = property;
                cropNameObj.value = cropNames[property];
                vm.cropNames.push(cropNameObj);
            }
        }
    );

    agUtils.getCropValues().then(
        (response) => {
            vm.cropValues = response.jsonValue;
        }
    );

    function reset() {
        if (vm.subscription) {
            vm.ctx.subscriptionApi.removeSubscription(vm.subscription.id);
        }
        vm.cropLevelFlotSubscriptionsInfo = null;
        vm.cropLevel = 0;
        vm.cropLevelTs = "";
        vm.cropLevelLabel = "";
        vm.statusId = 0;
        vm.statusMessage = "ag.silo.status-unknown";
        vm.cropWeight = 0;
        vm.cropVolumePercentage = 0;
    }

    function onDataUpdated(subscription, apply) {
        var data = subscription.data;
        let attributes = {}
        for (var item of data) {
            let key = item.dataKey.name
            let lastValue = item.data[item.data.length - 1]
            attributes[key] = item.data.length > 0 ? lastValue[1] : null
        }
        
        vm.cropData = {
            device: vm.silo,
            algorithm: attributes.algorithm,
            deviceType: attributes.deviceType,
            measurePoints: vm.measurePoints,
            cropPoints: attributes['crop-points']
        };
        
        vm.siloData = {
            silo: vm.silo,
            sensorLines: [],
            levelIndicators: vm.levelIndicatorDevices,
            cropData: angular.copy(vm.cropData)
        };
        

        
        vm.minCropLevel = attributes['crop-level-min'];
        vm.avgCropLevel = attributes['crop-level-avg'];
        vm.maxCropLevel = attributes['crop-level-max'];
        
        vm.currentCropVolume = attributes['crop-volume']
        let maxCropVolume = attributes['silo-volume']
        vm.cropVolumePercentage = null
        if (maxCropVolume !== null && vm.currentCropVolume !== null) {
            vm.cropVolumePercentage = 100 * vm.currentCropVolume / maxCropVolume;
        }
        vm.cropWeight = attributes['crop-weight'];
        
        vm.cropLevel = vm.avgCropLevel;
        // TODO: unsure if we still should use maxCropLevel
        vm.maxCropLevelIndicator = vm.silo.totalheight // vm.silo.maxCropLevel ?? vm.silo.totalheight
        vm.cropLevelIndicatorPct = parseFloat(((vm.cropLevel / vm.maxCropLevelIndicator) * 100).toFixed(2));

        vm.maxCroplevelThresholdPct = 0;
        vm.minCroplevelThresholdPct = 0;
        if (angular.isDefined(vm.maxCropLevelThreshold) && vm.maxCropLevelThreshold !== null) {
            vm.maxCroplevelThresholdPct = parseFloat(((1 - (vm.maxCropLevelThreshold / vm.maxCropLevelIndicator)) * 100).toFixed(2));
        }
        if (angular.isDefined(vm.minCropLevelThreshold) && vm.minCropLevelThreshold !== null) {
            vm.minCroplevelThresholdPct = parseFloat((vm.minCropLevelThreshold / vm.maxCropLevelIndicator) * 100).toFixed(2);
        }

        if (apply) {
            $scope.$digest();
        }
        vm.loading = false;
        vm.dataReady = true;
    }
    vm.dataReady = false;
     

    function onDataUpdateError(subscription, e) {
        $log.error(e);
    }

    function loadSilo() {
        vm.silo = null;
        vm.levelIndicator = null;
        vm.siloData = null;
        reset();
        if (vm.siloId) {
            vm.loading = true;
            siloService.getSilo(vm.siloId).then(
                (silo) => {
                    vm.silo = silo;
                    if (angular.isDefined(vm.silo.maxCropLevelThreshold) && vm.silo.maxCropLevelThreshold !== null) {
                        vm.cropLevelLegendConfig.maxThresholdLabel = $translate.instant('ag.silo.max-level-threshold');
                        vm.maxCropLevelThreshold = vm.silo.maxCropLevelThreshold;
                    }
                    if (angular.isDefined(vm.silo.minCropLevelThreshold) && vm.silo.minCropLevelThreshold !== null) {
                        vm.cropLevelLegendConfig.minThresholdLabel = $translate.instant('ag.silo.min-level-threshold');
                        vm.minCropLevelThreshold = vm.silo.minCropLevelThreshold;
                    }
                    attributeService.getEntityLatestTimeseriesValues(vm.siloId.entityType, vm.siloId.id, [agTypes.telemetry.silo.cropType], { ignoreLoading: true }).then(
                        (cropResponse) => {
                            vm.silo.cropType = null;
                            var cropType = cropResponse.crop_type[0].value;
                            if (vm.cropNames) {
                                var currentCropName = $filter('filter')(vm.cropNames, {nameId: cropType}, true);
                                if (currentCropName && currentCropName.length) {
                                    vm.silo.cropType = currentCropName[0];
                                    if (vm.cropValues && vm.cropValues[cropType]) {
                                        var resolvedDensity = Number.isFinite(vm.silo.customCropDensity) ? vm.silo.customCropDensity : vm.cropValues[cropType].density;
                                        var resolvedAngle = Number.isFinite(vm.silo.customCropGlidingAngle) ? vm.silo.customCropGlidingAngle : vm.cropValues[cropType].gliding_angle;
                                        vm.silo.cropDensity = resolvedDensity;
                                        vm.silo.glidingAngle = resolvedAngle;
                                    }
                                }
                                vm.cropLabel = vm.silo.cropType ? "(" + $translate.instant(agTypes.cropNames[vm.silo.cropType.value.name]) + ")" : "";
                            }
                            reloadSiloLevelIndicator();
                        },
                        () => {
                            vm.loading = false;
                        }
                    );
                },
                () => {
                    vm.loading = false;
                }
            );
        }
    }

    function reloadSiloLevelIndicator() {
        if (vm.siloId) {
            updateChartSubscription(vm.silo);
            vm.loading = false;
            vm.hideCropLevel = false;
        }
    }

    function updateChartSubscription(silo) {
        vm.chartSubscriptionsInfo = [{
            type: types.datasourceType.entity,
            entityType: types.entityType.asset,
            entityId: silo.id.id,
            entityName: silo.name,
            timeseries: [
                {
                    name: agTypes.telemetry.silo.cropLevelAvg,
                    label: $translate.instant('ag.silo.crop-level'),
                    color: agTypes.colors.maxColor
                }
            ]
        }];
    }

    function displayValueTooltip($event, value, units, timestamp, disableColorFunc) {
        displayTelemetryTooltip($event, $scope, $compile, value, value + units, timestamp, tempColor(value, disableColorFunc));
    }

    function tempColor(val, disableColorFunc) {
        if (!disableColorFunc) {
            if (angular.isDefined(vm.maxCropLevelThreshold) && angular.isDefined(vm.maxCropLevelThreshold) !== null && val >= vm.maxCropLevelThreshold) {
                return agTypes.colors.errorColor;
            }
            if (angular.isDefined(vm.minCropLevelThreshold) && angular.isDefined(vm.minCropLevelThreshold) !== null && val <= vm.minCropLevelThreshold) {
                return agTypes.colors.errorColor;
            }
            return agTypes.colors.normalGreyColor;
        }
        return agTypes.colors.normalGreyColor;
    }

    function getStatusColor() {
        if (vm.statusId === 2 || vm.statusId === 6) {
            return "#27ae60";
        } else if (vm.statusId === 1 || vm.statusId === 8) {
            return "#ffb72d";
        } else {
            return "#d53d0e";
        }
    }
}
